import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, X, HelpCircle, ArrowRight, TrendingUp } from 'lucide-react';
import { EASE_OUT } from '../../lib/anim';

// Static animation configs
const ONCE = { once: true };
const HEADER_REVEAL = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5 },
};
const EXPAND_ANIM = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  transition: { duration: 0.35, ease: EASE_OUT },
};
const CARD_VIEWPORT = { once: true, margin: '-60px' };
const CARD_INITIAL = { opacity: 0, y: 40 };
const CARD_IN_VIEW = { opacity: 1, y: 0 };
const RING_TRANSITION = { duration: 1.2, ease: EASE_OUT, delay: 0.3 };
const CHEVRON_TRANSITION = { duration: 0.2 };
const BAR_INITIAL = { width: 0 };
const BAR_TRANSITION = { duration: 0.8, delay: 0.2 };

const FIT_CONFIG = {
  strong_fit: { color: '#10B981', label: 'Strong Fit', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.35)' },
  possible_fit: { color: '#10B981', label: 'Possible Fit', bg: 'rgba(16,185,129,0.05)', border: 'rgba(16,185,129,0.15)' },
  conditional_fit: { color: '#F59E0B', label: 'Conditional', bg: 'rgba(245,158,11,0.05)', border: 'rgba(245,158,11,0.15)' },
  business_review: { color: '#60A5FA', label: 'Business Review Recommended', bg: 'rgba(96,165,250,0.08)', border: 'rgba(96,165,250,0.25)' },
  insufficient_data: { color: '#94A3B8', label: 'Needs More Data', bg: 'rgba(148,163,184,0.05)', border: 'rgba(148,163,184,0.15)' },
};

const GROUP_FLAGS = {
  'Canada': '\u{1F1E8}\u{1F1E6}', 'Australia': '\u{1F1E6}\u{1F1FA}', 'United Kingdom': '\u{1F1EC}\u{1F1E7}',
  'New Zealand': '\u{1F1F3}\u{1F1FF}', 'Europe': '\u{1F1EA}\u{1F1FA}', 'Passport Options': '\u{1F6C2}',
};

const MEMBER_FLAGS = {
  'Germany': '\u{1F1E9}\u{1F1EA}', 'Portugal': '\u{1F1F5}\u{1F1F9}', 'Estonia': '\u{1F1EA}\u{1F1EA}',
  'Italy': '\u{1F1EE}\u{1F1F9}', 'France': '\u{1F1EB}\u{1F1F7}', 'Spain': '\u{1F1EA}\u{1F1F8}',
  'Netherlands': '\u{1F1F3}\u{1F1F1}', 'Greece': '\u{1F1EC}\u{1F1F7}', 'Croatia': '\u{1F1ED}\u{1F1F7}',
  'Malta': '\u{1F1F2}\u{1F1F9}', 'Cyprus': '\u{1F1E8}\u{1F1FE}', 'UAE': '\u{1F1E6}\u{1F1EA}',
};

const GROUP_IMAGES = {
  'Canada': 'https://images.pexels.com/photos/11456645/pexels-photo-11456645.jpeg?auto=compress&cs=tinysrgb&w=600',
  'Australia': 'https://images.pexels.com/photos/15571948/pexels-photo-15571948.jpeg?auto=compress&cs=tinysrgb&w=600',
  'United Kingdom': 'https://images.pexels.com/photos/36662554/pexels-photo-36662554.jpeg?auto=compress&cs=tinysrgb&w=600',
  'New Zealand': 'https://images.pexels.com/photos/696680/pexels-photo-696680.jpeg?auto=compress&cs=tinysrgb&w=600',
  'Europe': 'https://images.pexels.com/photos/2064827/pexels-photo-2064827.jpeg?auto=compress&cs=tinysrgb&w=600',
  'Passport Options': 'https://images.pexels.com/photos/3787839/pexels-photo-3787839.jpeg?auto=compress&cs=tinysrgb&w=600',
};

// Circular progress ring
const ProgressRing = ({ score, maxScore, color, size = 80 }) => {
  const pct = maxScore > 0 ? Math.min((score / maxScore) * 100, 100) : 0;
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          whileInView={{ strokeDashoffset: offset }}
          viewport={ONCE}
          transition={RING_TRANSITION}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: 'var(--font-heading)', fontSize: size > 70 ? '1.5rem' : '1.125rem',
          fontWeight: 700, color: '#fff', lineHeight: 1,
        }}>
          {score}
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.55rem',
          color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em',
        }}>
          / {maxScore}
        </span>
      </div>
    </div>
  );
};

const GateIcon = ({ passed }) => {
  if (passed === true) return <CheckCircle size={13} color="#10B981" />;
  if (passed === false) return <X size={13} color="#F43F5E" />;
  return <HelpCircle size={13} color="#B9AFA8" />;
};

const MemberRow = ({ member }) => {
  const cfg = FIT_CONFIG[member.fit_key] || FIT_CONFIG.insufficient_data;
  return (
    <div
      data-testid={`europe-member-${member.country.replace(/\s/g, '-').toLowerCase()}`}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        padding: '10px 12px', borderRadius: '2px',
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{MEMBER_FLAGS[member.country] || ''}</span>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '0.875rem', fontWeight: 700, color: '#fff' }}>
            {member.country}
          </span>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.55rem', fontWeight: 600,
            letterSpacing: '0.08em', padding: '2px 8px', borderRadius: '2px',
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
          }}>
            {cfg.label.toUpperCase()}
          </span>
        </div>
        {member.summary && (
          <span style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', marginTop: '3px', lineHeight: 1.5 }}>
            {member.summary.substring(0, 140)}{member.summary.length > 140 ? '...' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

const RecommendationCard = ({ rec, index, isExpanded, onToggle }) => {
  const cfg = FIT_CONFIG[rec.fit_key] || FIT_CONFIG.insufficient_data;
  const flag = GROUP_FLAGS[rec.group] || '';
  const hasScore = rec.score_range && rec.score_range.max_possible > 0;
  const bgImage = GROUP_IMAGES[rec.group];

  return (
    <motion.div
      data-testid={`top-match-card-${rec.group.replace(/\s/g, '-').toLowerCase()}`}
      initial={CARD_INITIAL}
      whileInView={CARD_IN_VIEW}
      viewport={CARD_VIEWPORT}
      transition={{ duration: 0.6, delay: index * 0.12, ease: EASE_OUT }}
      style={{
        position: 'relative',
        border: `1px solid ${isExpanded ? cfg.border : 'rgba(255,255,255,0.2)'}`,
        borderRadius: '2px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.3s, transform 0.3s',
      }}
      onClick={onToggle}
      onMouseEnter={e => { if (!isExpanded) { e.currentTarget.style.borderColor = cfg.border; e.currentTarget.style.transform = 'translateY(-2px)'; }}}
      onMouseLeave={e => { if (!isExpanded) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}}
    >
      {bgImage && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: isExpanded ? 0.3 : 0.45,
          transition: 'opacity 0.3s',
        }} />
      )}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: isExpanded
          ? 'linear-gradient(135deg, rgba(48,43,44,0.92) 0%, rgba(48,43,44,0.88) 100%)'
          : 'linear-gradient(135deg, rgba(48,43,44,0.82) 0%, rgba(48,43,44,0.75) 100%)',
        transition: 'background 0.3s',
      }} />

      {/* Card Header */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', alignItems: 'center', gap: '1.25rem',
        padding: '1.5rem 2rem',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <span style={{ fontSize: '2rem', lineHeight: 1 }}>{flag}</span>
            <div>
              <h3 style={{
                fontFamily: 'var(--font-heading)', fontSize: '1.25rem',
                fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.2,
              }}>
                {rec.group}
              </h3>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
                color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em',
              }}>
                {rec.tagline}
              </span>
            </div>
          </div>
          <span style={{
            display: 'inline-block',
            padding: '3px 12px',
            borderRadius: '2px',
            fontFamily: 'var(--font-mono)',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            color: cfg.color,
            background: cfg.bg,
            border: `1px solid ${cfg.border}`,
          }}>
            {(rec.display_label || cfg.label).toUpperCase()}
          </span>
          {rec.preferred && (
            <span style={{
              display: 'inline-block', marginLeft: '8px',
              padding: '3px 10px', borderRadius: '2px',
              fontFamily: 'var(--font-mono)', fontSize: '0.6rem', fontWeight: 600,
              letterSpacing: '0.08em', color: '#F67425',
              background: 'rgba(246,116,37,0.12)', border: '1px solid rgba(246,116,37,0.3)',
            }}>
              YOUR PREFERENCE
            </span>
          )}
        </div>

        {hasScore && (
          <ProgressRing
            score={rec.score_range.confirmed_min}
            maxScore={rec.score_range.max_possible}
            color={cfg.color}
          />
        )}

        <motion.div
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={CHEVRON_TRANSITION}
          style={{ color: 'rgba(255,255,255,0.6)' }}
        >
          <ArrowRight size={18} />
        </motion.div>
      </div>

      {/* One-line summary */}
      <div style={{
        position: 'relative', zIndex: 2,
        padding: '0 2rem 1rem',
        fontSize: '0.875rem',
        color: 'rgba(255,255,255,0.78)',
        lineHeight: 1.6,
      }}>
        {rec.summary?.substring(0, 140)}{rec.summary?.length > 140 ? '...' : ''}
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <motion.div
          {...EXPAND_ANIM}
          style={{ overflow: 'hidden', position: 'relative', zIndex: 2 }}
        >
          <div style={{
            padding: '0 2rem 2rem',
            borderTop: '1px solid rgba(255,255,255,0.2)',
            paddingTop: '1.5rem',
          }}>
            {/* Group members (Europe / Passport Options) */}
            {rec.members?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                  letterSpacing: '0.15em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.6)', marginBottom: '12px',
                }}>
                  Countries In This Group
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {rec.members.map(m => <MemberRow key={m.country} member={m} />)}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              {rec.requirements?.length > 0 && (
                <div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                    letterSpacing: '0.15em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.6)', marginBottom: '12px',
                  }}>
                    Requirements Check
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {rec.requirements.map(g => (
                      <div key={g.gate} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ marginTop: '2px', flexShrink: 0 }}><GateIcon passed={g.passed} /></div>
                        <div>
                          <span style={{
                            fontSize: '0.8125rem', color: '#fff',
                            opacity: g.passed === false ? 0.5 : 1,
                            textDecoration: g.passed === false ? 'line-through' : 'none',
                          }}>
                            {g.gate.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <span style={{
                            display: 'block', fontSize: '0.7rem',
                            color: 'rgba(255,255,255,0.6)', marginTop: '2px',
                          }}>
                            {g.note}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {rec.actions?.length > 0 && (
                <div>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                    letterSpacing: '0.15em', textTransform: 'uppercase',
                    color: 'var(--brand-purple)', marginBottom: '12px',
                  }}>
                    Recommended Actions
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {rec.actions.map(a => (
                      <div key={`action-${a.substring(0, 20)}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <TrendingUp size={13} color="var(--brand-purple)" style={{ marginTop: '3px', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 }}>{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {hasScore && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                  letterSpacing: '0.15em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.6)', marginBottom: '8px',
                }}>
                  Score Range — {rec.score_range.label}
                </div>
                <div style={{ position: 'relative', height: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: '4px', overflow: 'hidden' }}>
                  <motion.div
                    initial={BAR_INITIAL}
                    whileInView={{ width: `${(rec.score_range.probable_max / rec.score_range.max_possible) * 100}%` }}
                    viewport={ONCE}
                    transition={BAR_TRANSITION}
                    style={{ position: 'absolute', left: 0, top: 0, height: '100%', background: `${cfg.color}30`, borderRadius: '4px' }}
                  />
                  <motion.div
                    initial={BAR_INITIAL}
                    whileInView={{ width: `${(rec.score_range.confirmed_min / rec.score_range.max_possible) * 100}%` }}
                    viewport={ONCE}
                    transition={BAR_TRANSITION}
                    style={{ position: 'absolute', left: 0, top: 0, height: '100%', background: cfg.color, borderRadius: '4px' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: cfg.color }}>
                    {rec.score_range.confirmed_min}
                    {rec.score_range.probable_max > rec.score_range.confirmed_min && ` — ${rec.score_range.probable_max}`}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' }}>
                    {rec.score_range.max_possible} max
                  </span>
                </div>
              </div>
            )}

            {rec.conditions?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '1rem' }}>
                {rec.conditions.map(c => (
                  <span key={c} style={{
                    fontSize: '0.7rem', padding: '3px 10px',
                    background: 'rgba(245,158,11,0.08)',
                    borderRadius: '2px',
                    color: '#F59E0B',
                    border: '1px solid rgba(245,158,11,0.2)',
                  }}>{c}</span>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Fallback: build country-level cards from legacy lists (no program names shown)
const buildFallbackRecommendations = (assessment) => {
  const FIT_MAP = { likely_fit: 'strong_fit', possible_fit: 'possible_fit', conditional_fit: 'conditional_fit' };
  const RANK = { strong_fit: 0, possible_fit: 1, conditional_fit: 2, insufficient_data: 3 };
  const all = [
    ...(assessment.pr_countries || []).map(c => ({ ...c, fitKey: FIT_MAP[c.pr_fit] || 'insufficient_data' })),
    ...(assessment.wp_countries || []).map(c => ({ ...c, fitKey: FIT_MAP[c.wp_fit] || 'insufficient_data' })),
  ];
  const seen = new Map();
  for (const m of all) {
    const ex = seen.get(m.country);
    if (!ex || RANK[m.fitKey] < RANK[ex.fitKey]) seen.set(m.country, m);
  }
  return [...seen.values()]
    .sort((a, b) => RANK[a.fitKey] - RANK[b.fitKey])
    .slice(0, 5)
    .map(m => ({
      group: m.country,
      fit_key: m.country === 'United Kingdom' ? 'business_review' : m.fitKey,
      display_label: m.country === 'United Kingdom' ? 'Business Review Recommended' : undefined,
      tagline: m.track_label || '',
      summary: m.notes,
      score_range: m.score_range,
      requirements: m.hard_gates,
      actions: m.next_actions,
      conditions: m.conditions,
      members: [],
    }));
};

const TopMatches = ({ assessment }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  const recommendations = assessment.recommendations?.length
    ? assessment.recommendations
    : buildFallbackRecommendations(assessment);

  if (recommendations.length === 0) return null;

  return (
    <section
      data-testid="top-matches-section"
      style={{
        padding: '0 1.5rem 4rem',
        maxWidth: '900px',
        margin: '0 auto',
      }}
    >
      <motion.div
        {...HEADER_REVEAL}
        style={{ marginBottom: '2rem' }}
      >
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: '0.7rem', fontWeight: 700,
          letterSpacing: '0.25em', textTransform: 'uppercase',
          color: '#F67425', marginBottom: '0.75rem',
        }}>
          Axpats Recommendation
        </div>
        <h2 style={{
          fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.5rem, 3vw, 2rem)',
          fontWeight: 800, color: '#302B2C', letterSpacing: '-0.03em',
          lineHeight: 1.05, margin: 0,
        }}>
          Countries where your profile is <span style={{ background: 'linear-gradient(135deg, #F67425, #BB0518)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>strongest</span>
        </h2>
      </motion.div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {recommendations.map((rec, i) => (
          <RecommendationCard
            key={rec.group}
            rec={rec}
            index={i}
            isExpanded={expandedIdx === i}
            onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)}
          />
        ))}
      </div>
    </section>
  );
};

export default TopMatches;
